import type { ReactNode } from 'react';
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import type { ProviderName } from '@atlaskit/editor-common/provider-factory';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { EmojiResource } from '@atlaskit/emoji';
import type { EmojiLoadSuccessCallback, EmojiLoadFailCallback } from '@atlaskit/emoji/resource';
import type { RendererAppearance, RendererProps, ExtensionViewportSize } from '@atlaskit/renderer';
import { RendererWithAnalytics as AkRenderer } from '@atlaskit/renderer';
import type { ObjectKey, TaskState } from '@atlaskit/task-decision';
import { fg } from '@atlaskit/platform-feature-flags';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { isMobileApp_DO_NOT_USE } from '@confluence/mobile-app-detection-common';
import { getRendererSmartButtonExtensionHandlers } from '@confluence/automation-extensions/entry-points/getRendererSmartButtonExtensionHandlers.ts';
import { ContentRefetchHandler } from '@confluence/content-body/entry-points/ContentRefetchHandler.ts';
import { getContentRefetchHandlerProps } from '@confluence/content-body/entry-points/getContentRefetchHandlerProps.ts';
import { useStoreOptions } from '@confluence/content-smartlinks/entry-points/useStoreOptions.ts';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext.ts';
import { getRendererCustomSitesExtensionHandlers } from '@confluence/custom-sites-extensions/entry-points/getRendererCustomSitesExtensionHandlers.ts';
import { useEditorFeatureFlags } from '@confluence/editor-shared/entry-points/useEditorFeatureFlags.ts';
import { useProfilecardProvider } from '@confluence/editor-shared/entry-points/useProfilecardProvider.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { LazySmartlinkAuthNudge } from '@confluence/experiment-smartlink-auth-nudge/entry-points/LazySmartlinkAuthNudge.tsx';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import { RendererAnalyticsListener } from '@confluence/fabric-analytics-listeners/entry-points/RendererAnalyticsListener.ts';
import type { CreateEmojiProviderParametersType } from '@confluence/fabric-emoji-support';
import { createEmojiProviderParameters } from '@confluence/fabric-emoji-support';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers.ts';
import { shouldDisplayExtensionAsInline } from '@confluence/fabric-extension-lib/entry-points/shouldDisplayExtensionAsInline.ts';
import type { TaskAction } from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider.ts';
import type { ProfileCardProvider } from '@confluence/fabric-media-support/entry-points/types.ts';
import { useMediaSSRModeAndAuthConfig } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers.ts';
import {
	ConfluenceTaskDecisionProvider,
	TaskBatcher,
} from '@confluence/fabric-media-support/entry-points/TaskDecisionProvider.ts';
import { createMentionsConfig } from '@confluence/fabric-media-support/entry-points/MentionsProvider.ts';
import {
	createRendererMediaProvider,
	controlDataExportOnCertainFeatures,
} from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import {
	getEmojiConfig,
	getEmojiProvider,
} from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { useRendererActionsDispatch } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useViewUserProfile } from '@confluence/global-operations/entry-points/useViewUserProfile.ts';
import { getLogger } from '@confluence/logger';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { usePageContentId, useContentType, usePageSpaceKey } from '@confluence/page-context';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { FeedFollowErrorDisplay } from '@confluence/profile/entry-points/FeedFollowErrorDisplay.ts';
import { useFeedFollow } from '@confluence/profile/entry-points/useFeedFollow.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy.ts';
import type { FeatureFlagsType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';
import { ConfluenceEditorCardProvider } from '@confluence/smartlinks';
import { CompetitorPrompt } from '@confluence/prompt-competitor-link/entry-points/PromptPopup.ts';
import { useMemoizedSyncedBlockProviderNodeComponent } from '@confluence/fabric-providers/entry-points/SyncedBlockProvider.ts';
import { useMediaCommentExtensions } from '@confluence/editor-features/entry-points/useMediaCommentExtensions.ts';
import { EmbeddedForgeMacroHost } from '@confluence/forge-ui/entry-points/EmbeddedForgeMacroHost.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { createFallbackMediaNameFetcher } from '@confluence/editor-features/entry-points/fallbackMediaNameFetcher.ts';

import {
	registerGlobalMediaViewedListener,
	removeGlobalMediaViewedListener,
} from './AttachmentEvents.tsx';
import { batchTaskUpdate } from './batchTaskUpdate.ts';

export const ANALYTICS_EVENT_SEVERITY_TRACKING = {
	enabled: true,
	severityDegradedThreshold: 4747,
	severityNormalThreshold: 3775,
};

const logger = getLogger('ADFRenderer');
export const UNSUPPORTED_CONTENT_THRESHOLDS = {
	enabled: true,
	thresholds: {
		blocking: 1.5,
		degraded: 1,
	},
};

export interface ADFRendererProps extends RendererProps {
	getExtensionHandlers?: (
		setLegacyRendererFlag?: ((extensionKey?: string) => void) | undefined,
	) => ExtensionHandlers;
	getDataProviders?: () => ProviderFactory;
	onRendered?: () => void;
	contentId?: string;
	contentType?: string;
	appearance?: RendererAppearance;
	portal?: HTMLDivElement;
	renderWhenReady?: () => ReactNode;
	allowStickyHeaders?: boolean;
	queryHash?: string;
	isTruncatedContent?: boolean;
	maxHeight?: number;
	fadeOutHeight?: number;
	mediaToken?: string;
	isContentHeaderFixedAtTop?: boolean;
	extensionViewportSizes?: ExtensionViewportSize[];
	onEmojiLoadSuccess?: EmojiLoadSuccessCallback;
	onEmojiLoadFail?: EmojiLoadFailCallback;
	getExtensionHeight?: GetPMNodeHeight;
	pageMode?: PageMode;
}

interface ADFRendererComponentProps extends ADFRendererProps {
	offsetTop: number;
	defaultScrollRootId_DO_NOT_USE?: string;
	shouldAddDefaultScrollRootOffsetTop_DO_NOT_USE?: boolean;
	flags: FlagsStateContainer;
	allowRendererContainerStyles?: boolean;
}

type ProviderDefinitions = {
	[name: string]: Promise<any>;
};

type MergeDataProvidersOptions = {
	intl: IntlShape;
	cloudId: string;
	userId: string | null;
	userRole?: 'admin' | 'trusted' | 'basic';
	contentId: string;
	spaceKey: string;
	pageContextContentId?: string;
	flags: FlagsStateContainer;
	featureFlags: FeatureFlagsType;

	providerFactory?: ProviderFactory;
	getDataProviders?: () => ProviderFactory;
	profilecardProvider: Promise<ProfileCardProvider>;

	isExternalShare: boolean;
	hasViewUserProfilePermission: boolean;

	handleActionToggle: (objectKey: ObjectKey, state: TaskState) => void;
	handleInviteItemClick?: () => void;
	mediaToken?: string;
	emojiResourceParams?: CreateEmojiProviderParametersType;
};

const mergeDataProviders: (options: MergeDataProvidersOptions) => {
	providerFactory: ProviderFactory | undefined;
	providers: ProviderDefinitions;
} = ({
	hasViewUserProfilePermission,
	handleActionToggle,
	handleInviteItemClick,
	getDataProviders,
	providerFactory,
	isExternalShare,
	contentId,
	spaceKey,
	pageContextContentId,
	cloudId,
	userId,
	userRole,
	flags,
	profilecardProvider,
	intl,
	mediaToken,
	emojiResourceParams,
}) => {
	const providers: ProviderDefinitions = {};

	if (getDataProviders) {
		Object.assign(providers, getDataProviders());
	}

	if (contentId || mediaToken) {
		providers.mediaProvider = createRendererMediaProvider({
			contentId,
			spaceKey,
			mediaDspContentId: pageContextContentId,
			isExternalShare,
			mediaToken,
		});

		providers.taskDecisionProvider = Promise.resolve(
			new ConfluenceTaskDecisionProvider(intl, flags, contentId, handleActionToggle),
		);
	}

	if (cloudId && userId) {
		providers.mentionProvider = createMentionsConfig(
			cloudId,
			userId,
			undefined,
			undefined,
			handleInviteItemClick,
			userRole,
		)
			.then((config) => config.mentionResource)
			.catch((err) => {
				if ((process as any).env.NODE_ENV !== 'production') {
					logger.error`Failed to initialise mention resource ${err}`;
				}
				return Promise.reject();
			});
	}

	if (emojiResourceParams && cloudId) {
		providers.emojiProvider = getEmojiProvider(EmojiResource, {
			...emojiResourceParams,
			allowAnonymousAccess: true,
		});
	}

	if (cloudId) {
		// If global view user profile is enabled then user can view profile card
		if (hasViewUserProfilePermission) {
			providers.profilecardProvider = profilecardProvider;
		}
	}

	if (contentId && spaceKey) {
		providers.contextIdentifierProvider = Promise.resolve({
			containerId: spaceKey,
			objectId: contentId,
			product: 'confluence',
		});
	}

	providers.cardProvider = Promise.resolve(new ConfluenceEditorCardProvider());

	const keys = Object.keys(providers);

	if (keys.length) {
		providerFactory = providerFactory || ProviderFactory.create({});
		for (const provider of Object.keys(providers)) {
			providerFactory.setProvider(provider as ProviderName, providers[provider]);
		}
	}

	return {
		providerFactory,
		providers,
	};
};

const projectLinkerHandlers = {
	'com.atlassian.confluence.project-linker': () => <></>,
};

export const unsupportedContentLevelsTrackingOptions =
	(): RendererProps['unsupportedContentLevelsTracking'] =>
		fg('platform_renderer_unsupported_content_tracking')
			? UNSUPPORTED_CONTENT_THRESHOLDS
			: undefined;

const DEFAULT_EXTENSION_HANDLERS = {};
const DEFAULT_GET_EXTENSION_HANDLERS = () => DEFAULT_EXTENSION_HANDLERS;
const DEFAULT_EXTENSION_VIEWPORT_SIZES = [];

export const ADFRendererComponent = memo(
	(props: ADFRendererComponentProps): React.JSX.Element | null => {
		const {
			document,
			dataProviders,
			extensionHandlers = DEFAULT_EXTENSION_HANDLERS,
			getExtensionHandlers = DEFAULT_GET_EXTENSION_HANDLERS,
			eventHandlers,
			appearance,
			contentMode,
			adfStage = 'stage0',
			portal,
			onRendered,
			renderWhenReady,
			allowStickyHeaders = true,
			allowPlaceholderText = false,
			isTruncatedContent,
			maxHeight,
			fadeOutHeight,
			flags,
			offsetTop,
			defaultScrollRootId_DO_NOT_USE,
			shouldAddDefaultScrollRootOffsetTop_DO_NOT_USE,
			mediaToken,
			onError,
			UNSTABLE_allowTableAlignment = true,
			UNSTABLE_allowTableResizing = true,
			UNSTABLE_isPresentational = false,
			extensionViewportSizes = DEFAULT_EXTENSION_VIEWPORT_SIZES,
			onEmojiLoadSuccess,
			onEmojiLoadFail,
			allowRendererContainerStyles,
			pageMode,
			...rest
		} = props;
		const intl = useIntl();
		const [isTaskToggled, setIsTaskToggled] = useState<boolean>(false);

		const initialState = window['__SSR_SMARTLINKS__'];
		const isPageSSR = isPageSSRd();
		const { storeOptions, SuspenseWrapperForUrl } = useStoreOptions(initialState, isPageSSR, {
			useSSRStore: expVal('confluence_smartlinks_suspense', 'isEnabled', false),
		});

		const isEmbedded = isEmbeddedConfluence_DO_NOT_USE();

		// Fix for HOT-99954 - Issue where embedded pages SSR SmartLinks open in same tab
		const shouldSSRsmartLinks = isPageSSR && !isEmbedded && !isMobileApp_DO_NOT_USE();

		const {
			cloudId,
			userId,
			featureFlags,
			isLicensed,
			isLoggedIn,
			isAdminHubAIEnabled,
			timeZone,
			tenantTimeZone,
		} = useSessionData();

		const [spaceKey] = usePageSpaceKey();
		const [pageContextContentId] = usePageContentId();
		const [pageContextContentType] = useContentType();

		const { openCoreInvites, userRole } = useContext(CoreInvitesContext);

		const [shouldRefetchContent, setShouldRefetchContent] = useState(false);

		const contentId = props.contentId || pageContextContentId;
		const contentType = props.contentType || pageContextContentType;

		const { canViewUserProfile: hasViewUserProfilePermission } = useViewUserProfile();
		const renderServerPlaceholder = useRenderServerPlaceholder();
		const taskBatcherRef = useRef(
			new TaskBatcher(expVal('confluence_frontend_task_batching_interval', 'value', 3000)),
		);
		const feedFollowData = useFeedFollow();

		// Handlers for batched task update
		const handleBatchTaskUpdate = useCallback(
			(batch: TaskAction[]) => {
				// @ts-ignore FIXME: `contentId` can be `undefined` here, and needs proper handling
				void batchTaskUpdate(contentId, batch).then(() => {
					setIsTaskToggled(false);
					setShouldRefetchContent(true);
				});
			},
			[contentId, setShouldRefetchContent],
		);

		const handleActionToggle = useCallback(
			(objectKey: ObjectKey, state: TaskState) => {
				setIsTaskToggled(true);
				taskBatcherRef.current.add({ objectKey, state });
			},
			[taskBatcherRef],
		);

		useEffect(() => {
			// there is a document check - we do not want to call registerGlobalMediaViewedListener
			// with no content
			if (document) {
				registerGlobalMediaViewedListener({ contentType });
			}

			onRendered && onRendered();

			taskBatcherRef.current.onFlush(handleBatchTaskUpdate);

			return () => {
				if (document) {
					removeGlobalMediaViewedListener();
				}
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [contentId, taskBatcherRef, handleBatchTaskUpdate]);

		useEffect(() => {
			setShouldRefetchContent(false);
			// WS-4098 - When the contentId changes or when we mount we don't need to refetch
			/* eslint-disable-next-line react-hooks/exhaustive-deps */
		}, [contentId]);

		useEffect(() => {
			const handleBeforeUnload = (event: BeforeUnloadEvent) => {
				if (isTaskToggled) {
					event.preventDefault();
					event.returnValue = ''; // This triggers the alert in all browsers
				}
			};

			window.addEventListener('beforeunload', handleBeforeUnload);
			return () => {
				window.removeEventListener('beforeunload', handleBeforeUnload);
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isTaskToggled, contentId]);

		// Setting up emoji resources for ssr and data providers
		const emojiResourceParams = useMemo(() => {
			if (cloudId) {
				return createEmojiProviderParameters({
					cloudId,
					userId,
					isLicensed,
					isLoggedIn,
					onEmojiLoadSuccess,
					onEmojiLoadFail,
				});
			}
		}, [cloudId, userId, isLicensed, isLoggedIn, onEmojiLoadSuccess, onEmojiLoadFail]);

		const emojiResourceConfig = useMemo(() => {
			if (emojiResourceParams) {
				return getEmojiConfig(
					emojiResourceParams.cloudId,
					emojiResourceParams.userId,
					emojiResourceParams.disableUpload,
					emojiResourceParams.emojiSSRConfig,
					undefined,
					emojiResourceParams.emojiLoadCallbacks,
				);
			}
		}, [emojiResourceParams]);

		const { isExternalShareRequest } = useContext(ExternalShareContext);

		const { push } = useRouteActions();

		const mergedExtensionHandlers = useMemo(() => {
			return {
				...getExtensionHandlers?.(),
				...extensionHandlers,
				...getRendererCustomSitesExtensionHandlers({ contentId }),
				...getRendererSmartButtonExtensionHandlers({
					contentId,
					contentType,
					cloudId,
				}),
				...projectLinkerHandlers,
			};
		}, [extensionHandlers, getExtensionHandlers, contentId, contentType, cloudId]);

		const mediaSSR = useMediaSSRModeAndAuthConfig({
			contentId,
			spaceKey,
			isExternalShare: isExternalShareRequest,
			mediaToken,
		});

		// -- Matches fabric-feature-flags.ts

		const isStickyHeadersEnabled = allowStickyHeaders;

		const errorHandler = useCallback(
			(error: Error) => {
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (process.env.REACT_SSR) {
					getMonitoringClient().submitError(error, {
						attribution: Attribution.BACKBONE,
					});
				}
				onError?.(error);
			},
			[onError],
		);

		const { shimAdfUrls } = useNavigationPolicy();

		const editorFeatureFlags = useEditorFeatureFlags();

		const profilecardProvider = useProfilecardProvider({
			push,
			userId,
			cloudId,
			featureFlags,
		});

		const shouldControlDataExport = controlDataExportOnCertainFeatures(window.location.pathname);

		// ----------------------------------

		const allowHeadingAnchorLinks = useMemo(
			() => ({
				allowNestedHeaderLinks: true,
				activeHeadingId: props.queryHash,
			}),
			[props.queryHash],
		);

		// Build media viewer extensions to add the comments button to the preview header
		const mediaViewerExtensions = useMediaCommentExtensions({
			isViewPage: true,
			analyticsSource: 'viewPageScreen',
			flags,
		});

		const fallbackMediaNameFetcher = useMemo(
			() => (contentId ? createFallbackMediaNameFetcher(contentId) : undefined),
			[contentId],
		);

		const media = useMemo(
			() => ({
				allowLinking: true,
				allowCaptions: true,
				featureFlags: { mediaInline: true },
				mediaViewerExtensions,
				ssr: mediaSSR,
				fallbackMediaNameFetcher: expVal('platform_editor_media_name_fallback', 'isEnabled', false)
					? fallbackMediaNameFetcher
					: undefined,
			}),
			[mediaSSR, mediaViewerExtensions, fallbackMediaNameFetcher],
		);

		const CompetitorPromptWithAppearance = useMemo(() => {
			return (competitorPromptProps: { sourceUrl: string; linkType?: string }) => (
				<CompetitorPrompt {...competitorPromptProps} editorAppearance={appearance} />
			);
		}, [appearance]);

		const actionOptions = useMemo(() => {
			const isFullPage =
				(appearance === 'full-page' || appearance === 'full-width' || appearance === 'max') &&
				(fg('platform_sl_3p_auth_rovo_action_kill_switch') ||
					fg('platform_sl_3p_auth_inline_tailored_cta_killswitch'));

			if (isFullPage) {
				return { hide: false, rovoChatAction: { optIn: true } };
			}
		}, [appearance]);

		const getResolvingPlaceholder = useCallback(
			(url: string): string | undefined => window.__SSR_SMARTLINKS_TITLES__?.[url],
			[],
		);

		const smartLinks = useMemo(
			() => ({
				ssr: shouldSSRsmartLinks,
				hideHoverPreview: false,
				CompetitorPrompt: CompetitorPromptWithAppearance,
				SuspenseWrapperForUrl,
				...(fg('platform_sl_3p_auth_rovo_action_kill_switch') ||
				fg('platform_sl_3p_auth_inline_tailored_cta_killswitch')
					? { actionOptions }
					: undefined),
				...(expVal('confluence_frontend_smartlinks_title_while_loading', 'isEnabled', false)
					? { getResolvingPlaceholder }
					: undefined),
			}),
			[
				actionOptions,
				shouldSSRsmartLinks,
				CompetitorPromptWithAppearance,
				SuspenseWrapperForUrl,
				getResolvingPlaceholder,
			],
		);

		const stickyHeaders = useMemo(
			() =>
				isStickyHeadersEnabled
					? {
							offsetTop,
							defaultScrollRootId_DO_NOT_USE,
							shouldAddDefaultScrollRootOffsetTop_DO_NOT_USE,
						}
					: undefined,
			[
				isStickyHeadersEnabled,
				offsetTop,
				defaultScrollRootId_DO_NOT_USE,
				shouldAddDefaultScrollRootOffsetTop_DO_NOT_USE,
			],
		);

		const mergedDataProviders = useMemo(
			() =>
				mergeDataProviders({
					// @ts-ignore FIXME: `contentId` can be `undefined` here, and needs proper handling
					contentId,
					// @ts-ignore FIXME: `spaceKey` can be `undefined` here, and needs proper handling
					spaceKey,
					pageContextContentId,
					cloudId,
					userId,
					flags,
					featureFlags,
					push,
					intl,
					providerFactory: dataProviders,
					profilecardProvider,
					isExternalShare: isExternalShareRequest,
					handleActionToggle,
					hasViewUserProfilePermission,
					handleInviteItemClick: openCoreInvites,
					userRole,
					mediaToken,
					emojiResourceParams,
				}),
			[
				contentId,
				spaceKey,
				pageContextContentId,
				cloudId,
				userId,
				flags,
				featureFlags,
				push,
				intl,
				dataProviders,
				profilecardProvider,
				isExternalShareRequest,
				handleActionToggle,
				hasViewUserProfilePermission,
				openCoreInvites,
				userRole,
				mediaToken,
				emojiResourceParams,
			],
		);

		const SyncedBlockNodeComponent = useMemoizedSyncedBlockProviderNodeComponent({
			doc: document,
			spaceKey,
			contentId,
			contentType,
			mergedDataProviderDefinitions: mergedDataProviders.providers,
			// Pass the same sticky header config as the page so tables in synced blocks use
			// the same scroll root (e.g. AkMainContent) and offset — required for sticky to work.
			stickyHeadersConfig: stickyHeaders ?? undefined,
			contentMode,
		});

		const nodeComponents = useMemo(() => {
			if (SyncedBlockNodeComponent) {
				return { syncBlock: SyncedBlockNodeComponent };
			}
		}, [SyncedBlockNodeComponent]);

		const isLivePage = useIsCurrentPageLive();

		// Store the finalDocument ADF in context so it can be accessed by comment editor
		// and other components (e.g. SmartlinkAuthNudge) without prop threading.
		const { setPageAdf } = useRendererActionsDispatch();

		useEffect(() => {
			if (appearance === 'full-page' || appearance === 'full-width' || appearance === 'max') {
				if (document) {
					const finalDocument = shimAdfUrls(document) as DocNode;
					setPageAdf(finalDocument);
				} else {
					setPageAdf(undefined);
				}
			}
		}, [appearance, document, setPageAdf, shimAdfUrls]);

		if (!document) {
			return null;
		}

		const finalDocument = shimAdfUrls(document) as DocNode;

		const content = (
			<>
				<RendererAnalyticsListener
					// @ts-ignore FIXME: `contentId` can be `undefined` here, and needs proper handling
					objectId={contentId}
					objectType={contentType}
				>
					<AkRenderer
						// This flag is used for fixing table width in SSR mode and for hydration
						// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
						enableSsrInlineScripts={Boolean(process.env.REACT_SSR)}
						// include renderer inline script from above on first render to prevent hydration mismatch
						noOpSSRInlineScript={renderServerPlaceholder}
						timeZone={timeZone || tenantTimeZone || undefined}
						adfStage={adfStage}
						document={finalDocument}
						appearance={appearance}
						contentMode={contentMode}
						stickyHeaders={stickyHeaders}
						extensionHandlers={mergedExtensionHandlers}
						eventHandlers={eventHandlers}
						featureFlags={editorFeatureFlags}
						dataProviders={mergedDataProviders.providerFactory}
						portal={portal}
						allowColumnSorting
						allowHeadingAnchorLinks={allowHeadingAnchorLinks}
						allowAltTextOnImages
						media={media}
						emojiResourceConfig={emojiResourceConfig}
						allowCopyToClipboard
						allowWrapCodeBlock
						{...rest}
						useSpecBasedValidator
						truncated={isTruncatedContent}
						maxHeight={maxHeight}
						fadeOutHeight={fadeOutHeight}
						analyticsEventSeverityTracking={ANALYTICS_EVENT_SEVERITY_TRACKING}
						unsupportedContentLevelsTracking={unsupportedContentLevelsTrackingOptions()}
						allowPlaceholderText={allowPlaceholderText}
						allowCustomPanels
						smartLinks={smartLinks}
						extensionViewportSizes={extensionViewportSizes}
						onError={errorHandler}
						UNSTABLE_allowTableAlignment={UNSTABLE_allowTableAlignment}
						UNSTABLE_allowTableResizing={UNSTABLE_allowTableResizing}
						UNSTABLE_isPresentational={UNSTABLE_isPresentational}
						allowRendererContainerStyles={allowRendererContainerStyles}
						shouldDisplayExtensionAsInline={shouldDisplayExtensionAsInline}
						allowFixedColumnWidthOption={
							fg('platform_editor_table_fixed_column_width_prop')
								? appearance === 'full-page' || appearance === 'full-width'
								: undefined
						}
						{...(nodeComponents && { nodeComponents })}
					/>
					{renderWhenReady && renderWhenReady()}
				</RendererAnalyticsListener>
				<ContentRefetchHandler
					{...getContentRefetchHandlerProps({
						contentId,
						shouldRefetch: shouldRefetchContent,
						updateShouldRefetchContent: ({ shouldRefetchContent: nextShouldRefetchContent }) =>
							setShouldRefetchContent(nextShouldRefetchContent),
					})}
				/>
				{pageMode &&
					pageMode === PageMode.VIEW &&
					!isEmbedded &&
					expValEquals('cc_smart_link_nudge_kill_switch', 'isEnabled', true) && (
						<LazySmartlinkAuthNudge
							contentId={contentId}
							contentType={contentType}
							isLivePage={isLivePage}
						/>
					)}
				<FeedFollowErrorDisplay
					feedConfigError={feedFollowData.feedConfigError}
					mutationError={feedFollowData.mutationError}
					showErrorFlag
				/>
				<EmbeddedForgeMacroHost document={document} flags={flags} />
			</>
		);

		return (
			<ConfluenceSmartCardProvider
				{...getSmartCardRenderers()}
				{...storeOptions}
				isAdminHubAIEnabled={isAdminHubAIEnabled}
				shouldControlDataExport={shouldControlDataExport}
				product="CONFLUENCE"
			>
				{content}
			</ConfluenceSmartCardProvider>
		);
	},
);
